import React from 'react';

import {Button, Icon, Input, Label, Menu, Text} from '@albert-io/atomic';
import {
  ASSIGNMENT_TYPE_FILTER_OPTIONS,
  CREATED_BY_FILTER_OPTIONS
} from 'client/TeacherDashboard/config/filterOptions';
import type {
  AssignmentTypeFilter,
  ClassworkCreatedByFilter,
  SelectOption
} from 'client/TeacherDashboard/types';

interface Props {
  createdBy: ClassworkCreatedByFilter;
  assignmentType: AssignmentTypeFilter;
  /** Due-date range bounds as YYYY-MM-DD (native date-input format); '' = unset. */
  dueDateStart: string;
  dueDateEnd: string;
  disabled?: boolean;
  onChange: (value: ClassworkCreatedByFilter) => void;
  onAssignmentTypeChange: (value: AssignmentTypeFilter) => void;
  onDueDateChange: (bound: 'start' | 'end', value: string) => void;
}

// A labelled group of single-select filter options rendered as check-marked menu items.
const FilterSection = <T extends string>({
  label,
  options,
  selected,
  onSelect
}: {
  label: string;
  options: ReadonlyArray<SelectOption<T>>;
  selected: T;
  onSelect: (value: T) => void;
}) => (
  <>
    <Menu.Item readonly textProps={{color: 'secondary', size: 's'}}>
      {label}
    </Menu.Item>
    {options.map((option) => (
      <Menu.Item
        key={option.value}
        selected={selected === option.value}
        rightIconProps={selected === option.value ? {icon: 'check'} : undefined}
        onClick={() => onSelect(option.value)}
      >
        {option.label}
      </Menu.Item>
    ))}
  </>
);

export const FiltersMenu = ({
  createdBy,
  assignmentType,
  dueDateStart,
  dueDateEnd,
  disabled,
  onChange,
  onAssignmentTypeChange,
  onDueDateChange
}: Props) => {
  const isActive =
    createdBy !== 'all' || assignmentType !== 'all' || !!dueDateStart || !!dueDateEnd;
  return (
    <Menu closeOnItemClick position='bottom-end' positionFixed>
      <Menu.Trigger>
        {(triggerProps) => (
          <Button
            {...triggerProps}
            color='secondary'
            disabled={disabled}
            variant='outlined'
            data-testid='filters-trigger'
          >
            <Icon className='mr-x1' color={isActive ? 'brand' : undefined} icon='bars-filter' />
            <Text color={isActive ? 'brand' : 'inherit'}>Filters</Text>
            {isActive && (
              <span aria-hidden className='ml-x1 h-x1 w-x1 rounded-full background-brand' />
            )}
          </Button>
        )}
      </Menu.Trigger>
      <Menu.Content>
        <FilterSection
          label='Created by'
          options={CREATED_BY_FILTER_OPTIONS}
          selected={createdBy}
          onSelect={onChange}
        />
        <Menu.Divider />
        <FilterSection
          label='Type'
          options={ASSIGNMENT_TYPE_FILTER_OPTIONS}
          selected={assignmentType}
          onSelect={onAssignmentTypeChange}
        />
        <Menu.Divider />
        {/* Raw date inputs (not Menu.Items) so interacting with them doesn't trip closeOnItemClick.
            Menu.Content's keydown handler hijacks arrows (segment stepping) and Tab (closes the
            menu) — keep those native inside this section; Escape still bubbles up to close. */}
        <div
          className='flex flex-col gap-x1 px-x2 py-x1'
          onKeyDown={(event) => {
            if (event.key !== 'Escape') {
              event.stopPropagation();
            }
          }}
        >
          <Text color='secondary' size='s'>
            Due date
          </Text>
          <Label className='flex flex-col gap-x0-half text-s'>
            Start
            <Input
              type='date'
              value={dueDateStart}
              max={dueDateEnd || undefined}
              onChange={(e) => onDueDateChange('start', e.target.value)}
            />
          </Label>
          <Label className='flex flex-col gap-x0-half text-s'>
            End
            <Input
              type='date'
              value={dueDateEnd}
              min={dueDateStart || undefined}
              onChange={(e) => onDueDateChange('end', e.target.value)}
            />
          </Label>
        </div>
      </Menu.Content>
    </Menu>
  );
};
